const UPC_SEARCH_INDEX_URL = (() => {
  const source = document.currentScript?.src;
  return source ? source.split('?')[0].replace(/predictive-search\.js$/, 'upc-search-index.json') : null;
})();

let upcSearchIndexPromise;
const loadUpcSearchIndex = () => {
  if (!upcSearchIndexPromise) {
    upcSearchIndexPromise = fetch(UPC_SEARCH_INDEX_URL, { credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error(`Search index: ${response.status}`);
      return response.json();
    });
  }
  return upcSearchIndexPromise;
};

const normalizeUpcSearch = (value) => String(value || '')
  .normalize('NFKD')
  .replace(/[\u0591-\u05C7]/g, '')
  .toLocaleLowerCase('he')
  .replace(/[|–—_./\\-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const upcSearchScore = (item, query) => {
  const title = normalizeUpcSearch(item.title);
  const vendor = normalizeUpcSearch(item.vendor);
  const handle = normalizeUpcSearch(item.handle);
  if (!`${title} ${vendor} ${handle}`.includes(query)) return Infinity;
  if (title === query) return 0;
  if (title.startsWith(query)) return 1;
  if (title.split(' ').some((word) => word.startsWith(query))) return 2;
  if (vendor.startsWith(query)) return 3;
  return title.includes(query) ? 4 : 5;
};

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-search]');
    this.panel = this.results?.closest('.predictive-search');
    this.status = this.querySelector('.predictive-search-status');
    this.request = 0;
    if (!this.input || !this.results || !this.panel) return;
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('focus', () => {
      loadUpcSearchIndex().catch(() => {});
      if (this.input.value.trim()) this.onInput(true);
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.addEventListener('focusout', () => setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 150));
  }

  onInput(immediate = false) {
    clearTimeout(this.timer);
    const term = this.input.value.trim();
    const request = ++this.request;
    if (!term) return this.close(true);
    this.message('מחפש…');
    this.timer = setTimeout(async () => {
      try {
        const index = await loadUpcSearchIndex();
        if (request === this.request && this.input.value.trim() === term) this.render(index, term);
      } catch (_error) {
        if (request === this.request) this.empty(term);
      }
    }, immediate ? 0 : 70);
  }

  rank(items, query) {
    return items.map((item) => ({ item, score: upcSearchScore(item, query) }))
      .filter(({ score }) => Number.isFinite(score))
      .sort((a, b) => a.score - b.score || a.item.title.localeCompare(b.item.title, 'he'))
      .map(({ item }) => item);
  }

  render(index, term) {
    const query = normalizeUpcSearch(term);
    const collections = this.rank(index.collections || [], query).slice(0, 2);
    const products = this.rank(index.products || [], query).slice(0, Math.max(4, 6 - collections.length));
    const matches = [...collections, ...products].slice(0, 6);
    if (!matches.length) return this.empty(term);
    this.results.replaceChildren();
    const list = document.createElement('div');
    list.className = 'upc-search-results';
    list.setAttribute('role', 'listbox');
    matches.forEach((item, indexNumber) => list.append(this.result(item, indexNumber)));
    list.append(this.allResults(term));
    this.results.append(list);
    this.open();
    this.announce(`${matches.length} הצעות נמצאו`);
  }

  result(item, indexNumber) {
    const link = document.createElement('a');
    link.className = 'upc-search-result';
    link.id = `upc-search-option-${indexNumber}`;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    link.href = item.type === 'collection'
      ? `/collections/${encodeURIComponent(item.handle)}`
      : `/products/${encodeURIComponent(item.handle)}`;
    const media = document.createElement('span');
    media.className = 'upc-search-result__media';
    if (item.image) {
      const image = new Image();
      image.src = item.image;
      image.alt = '';
      image.loading = 'lazy';
      media.append(image);
    }
    const copy = document.createElement('span');
    copy.className = 'upc-search-result__copy';
    const title = document.createElement('strong');
    title.textContent = item.title;
    const meta = document.createElement('small');
    meta.textContent = item.type === 'collection' ? `קטגוריה · ${item.count || 0} פריטים` : (item.vendor || 'מוצר');
    copy.append(title, meta);
    const arrow = document.createElement('span');
    arrow.textContent = '←';
    link.append(media, copy, arrow);
    return link;
  }

  allResults(term) {
    const link = document.createElement('a');
    link.className = 'upc-search-all';
    link.href = `${window.routes?.search_url || '/search'}?q=${encodeURIComponent(term)}`;
    link.textContent = `לכל התוצאות עבור “${term}”`;
    return link;
  }

  message(text) {
    this.results.replaceChildren();
    const message = document.createElement('div');
    message.className = 'upc-search-message';
    message.textContent = text;
    this.results.append(message);
    this.open();
  }

  empty(term) {
    this.message('לא נמצאו הצעות');
    this.results.append(this.allResults(term));
    this.announce('לא נמצאו הצעות');
  }

  installStyles() {
    if (document.getElementById('upc-predictive-search-styles')) return;
    const style = document.createElement('style');
    style.id = 'upc-predictive-search-styles';
    style.textContent = `
      .upc-search-results{direction:rtl;background:#fff}
      .upc-search-result{display:grid;grid-template-columns:48px minmax(0,1fr) 18px;gap:10px;align-items:center;min-height:62px;padding:7px 10px;border-bottom:1px solid #eee;color:#171717;text-decoration:none;text-align:right}
      .upc-search-result:hover,.upc-search-result[aria-selected="true"]{background:#f8f1f4}
      .upc-search-result__media{display:grid;place-items:center;width:48px;height:48px;background:#f7f7f7;overflow:hidden}
      .upc-search-result__media img{width:100%;height:100%;object-fit:contain}
      .upc-search-result__copy{display:flex;min-width:0;flex-direction:column;gap:3px}
      .upc-search-result__copy strong{display:-webkit-box;overflow:hidden;font-size:13px;line-height:1.35;-webkit-box-orient:vertical;-webkit-line-clamp:2}
      .upc-search-result__copy small{overflow:hidden;color:#777;font-size:11px;text-overflow:ellipsis;white-space:nowrap}
      .upc-search-all,.upc-search-message{display:block;padding:10px 12px;text-align:right;direction:rtl;background:#fff;font-size:12px}
      .upc-search-all{color:#73304f;font-weight:700;text-decoration:none}.upc-search-all:hover{background:#f8f1f4}
      .upc-search-message{color:#666}
    `;
    document.head.append(style);
  }

  open() {
    this.installStyles();
    const bottom = this.input.getBoundingClientRect().bottom;
    this.panel.style.cssText = `display:block!important;visibility:visible!important;opacity:1!important;position:absolute!important;z-index:99999!important;top:100%!important;right:0!important;left:auto!important;width:min(380px,calc(100vw - 20px))!important;min-width:0!important;max-height:${Math.max(220, innerHeight - bottom - 20)}px!important;background:#fff!important;border:1px solid #d8d8d8!important;box-shadow:0 10px 28px rgba(0,0,0,.16)!important;overflow:auto!important`;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close(clear = false) {
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.panel.style.removeProperty('display');
    if (clear) this.results.replaceChildren();
  }

  onKeydown(event) {
    if (event.key === 'Escape') return this.close();
    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
    const options = [...this.results.querySelectorAll('[role="option"]')];
    if (!options.length) return;
    const selected = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    if (event.key === 'Enter') {
      if (selected >= 0) { event.preventDefault(); options[selected].click(); }
      return;
    }
    event.preventDefault();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const next = selected < 0 ? (direction > 0 ? 0 : options.length - 1) : (selected + direction + options.length) % options.length;
    options.forEach((option, index) => option.setAttribute('aria-selected', index === next ? 'true' : 'false'));
    this.input.setAttribute('aria-activedescendant', options[next].id);
    options[next].scrollIntoView({ block: 'nearest' });
  }

  announce(message) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.setAttribute('aria-hidden', 'false');
    setTimeout(() => this.status.setAttribute('aria-hidden', 'true'), 1000);
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
